import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  List,
  ListItem,
  Chip,
  Paper,
  Grid,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { format } from 'date-fns';
import apiService from '../../../services/api';
import { dashboardTheme } from '../styles/dashboardTheme';

/**
 * CheckInHistoryDialog Component
 * Modal for viewing complete check-in history with trends
 */
const CheckInHistoryDialog = ({ open, onClose, token }) => {
  const [loading, setLoading] = useState(false);
  const [checkIns, setCheckIns] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && token) {
      fetchCheckInHistory();
    }
  }, [open, token]);

  const fetchCheckInHistory = async () => {
    setLoading(true);
    setError('');

    try {
      // Try the new template-based history endpoint first, then fall back to legacy
      let response;
      try {
        response = await apiService.getTemplateCheckInHistory(token);
        response = response?.history || response || [];
      } catch (e) {
        // Fall back to legacy endpoint
        console.log('Falling back to legacy check-in endpoint');
        response = await apiService.getCheckIns(token);
        response = response || [];
      }
      setCheckIns(response);
    } catch (err) {
      console.error('Error fetching check-in history:', err);
      setError('Unable to load check-in history. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper to check if a check-in is template-based (has dynamic responses)
  const isTemplateBasedCheckIn = (checkIn) => {
    return checkIn.dynamicResponses && checkIn.dynamicResponses.length > 0;
  };

  // Helper to get a display value for dynamic response
  const formatDynamicResponse = (response) => {
    if (response.fieldType === 'boolean') {
      return response.value ? 'Yes' : 'No';
    }
    if (response.fieldType === 'numeric_scale' || response.fieldType === 'emoji_scale' || response.fieldType === 'number') {
      return response.value;
    }
    if (response.fieldType === 'single_select') {
      return response.value?.replace(/_/g, ' ');
    }
    if (response.fieldType === 'multi_select' && Array.isArray(response.value)) {
      return response.value.join(', ');
    }
    return response.value;
  };

  // Helper to get short label for a field
  const getShortLabel = (fieldId) => {
    const labelMap = {
      'suds': 'SUDS',
      'presence': 'Presence',
      'flashback': 'Flashbacks',
      'nightmare': 'Nightmares',
      'grounding_used': 'Used Grounding',
      'grounding_technique': 'Technique',
      'mood': 'Mood',
      'anxiety': 'Anxiety',
      'notes': 'Notes'
    };
    return labelMap[fieldId] || fieldId.replace(/_/g, ' ');
  };

  const getMoodEmoji = (rating) => {
    const emojiMap = {
      1: '😢',
      2: '😔',
      3: '😐',
      4: '🙂',
      5: '😊'
    };
    return emojiMap[Math.round(rating)] || '😐';
  };

  const getAnxietyColor = (rating) => {
    if (rating <= 2) return '#66BB6A';
    if (rating <= 3) return '#FFA726';
    return '#EF5350';
  };

  const getAnxietyEmoji = (rating) => {
    const emojiMap = {
      1: '😌',  // Very calm
      2: '🙂',  // Calm
      3: '😐',  // Moderate
      4: '😟',  // Anxious
      5: '😰'   // Very anxious
    };
    return emojiMap[Math.round(rating)] || '😐';
  };

  const calculateTrend = (data, field) => {
    if (data.length < 2) return null;
    const recent = data.slice(0, 5).filter(item => item[field] !== undefined && item[field] !== null);
    const older = data.slice(5, 10).filter(item => item[field] !== undefined && item[field] !== null);
    
    if (older.length === 0 || recent.length === 0) return null;
    
    const recentAvg = recent.reduce((sum, item) => sum + item[field], 0) / recent.length;
    const olderAvg = older.reduce((sum, item) => sum + item[field], 0) / older.length;
    
    return recentAvg - olderAvg;
  };

  // Include both legacy check-ins (with mood/anxiety) and template-based check-ins
  const validCheckIns = checkIns.filter(c =>
    (c.moodRating !== undefined && c.moodRating !== null) ||
    isTemplateBasedCheckIn(c)
  );

  // Only calculate trends for legacy mood/anxiety check-ins
  const legacyCheckIns = checkIns.filter(c => c.moodRating !== undefined && c.moodRating !== null);
  const moodTrend = calculateTrend(legacyCheckIns, 'moodRating');
  const anxietyTrend = calculateTrend(legacyCheckIns, 'anxietyRating');

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon sx={{ color: dashboardTheme.colors.primary.main }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Check-in History
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            py: 6 
          }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography color="text.secondary">
              Loading check-in history...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="error">{error}</Typography>
            <Button onClick={fetchCheckInHistory} sx={{ mt: 2 }}>
              Retry
            </Button>
          </Box>
        ) : checkIns.length > 0 ? (
          <Box>
            {/* Statistics Overview */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <Paper elevation={0} sx={{ 
                  p: 2,
                  background: 'linear-gradient(135deg, rgba(255, 140, 0, 0.05) 0%, rgba(255, 140, 0, 0.02) 100%)',
                  border: '1px solid rgba(255, 140, 0, 0.1)',
                  borderRadius: '12px'
                }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Average Mood
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 600,
                      color: dashboardTheme.colors.primary.main  // Use AIMHI orange for mood
                    }}>
                      {validCheckIns.length > 0 
                        ? (validCheckIns.reduce((sum, c) => sum + c.moodRating, 0) / validCheckIns.length).toFixed(1)
                        : 'N/A'}
                    </Typography>
                    <Typography variant="h4">
                      {validCheckIns.length > 0 
                        ? getMoodEmoji(validCheckIns.reduce((sum, c) => sum + c.moodRating, 0) / validCheckIns.length)
                        : '❓'}
                    </Typography>
                    {moodTrend !== null && (
                      <Chip
                        size="small"
                        icon={moodTrend > 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                        label={moodTrend > 0 ? 'Improving' : 'Declining'}
                        color={moodTrend > 0 ? 'success' : 'warning'}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                {(() => {
                  const validAnxietyCheckIns = checkIns.filter(c => c.anxietyRating !== undefined && c.anxietyRating !== null);
                  const avgAnxiety = validAnxietyCheckIns.length > 0
                    ? validAnxietyCheckIns.reduce((sum, c) => sum + c.anxietyRating, 0) / validAnxietyCheckIns.length
                    : 0;
                  const anxietyColor = avgAnxiety > 0 ? getAnxietyColor(avgAnxiety) : '#9E9E9E';
                  
                  return (
                    <Paper elevation={0} sx={{ 
                      p: 2,
                      background: `linear-gradient(135deg, ${anxietyColor}15 0%, ${anxietyColor}05 100%)`,
                      border: `1px solid ${anxietyColor}20`,
                      borderRadius: '12px'
                    }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Average Anxiety
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <Typography variant="h4" sx={{ 
                          fontWeight: 600,
                          color: anxietyColor
                        }}>
                          {validAnxietyCheckIns.length > 0 
                            ? `${avgAnxiety.toFixed(1)}/5`
                            : 'N/A'}
                        </Typography>
                        {validAnxietyCheckIns.length > 0 && (
                          <Typography variant="h4">
                            {getAnxietyEmoji(avgAnxiety)}
                          </Typography>
                        )}
                        {anxietyTrend !== null && (
                          <Chip
                            size="small"
                            icon={anxietyTrend < 0 ? <TrendingDownIcon /> : <TrendingUpIcon />}
                            label={anxietyTrend < 0 ? 'Improving' : 'Increasing'}
                            color={anxietyTrend < 0 ? 'success' : 'warning'}
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    </Paper>
                  );
                })()}
              </Grid>
            </Grid>

            {/* Check-in List */}
            {(() => {
              // Include both legacy and template-based check-ins
              const validCheckInsForList = checkIns.filter(c =>
                (c.moodRating !== undefined && c.moodRating !== null) ||
                isTemplateBasedCheckIn(c)
              );

              if (validCheckInsForList.length === 0) {
                return (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      No check-ins found. Complete a check-in to see your history.
                    </Typography>
                  </Box>
                );
              }

              return (
                <>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Recent Check-ins
                  </Typography>
                  <List sx={{ py: 0 }}>
                    {validCheckInsForList.map((checkIn, index) => {
                      const isTemplateBased = isTemplateBasedCheckIn(checkIn);

                      return (
                        <ListItem
                          key={checkIn._id || index}
                          sx={{
                            px: 2,
                            py: 1.5,
                            background: index % 2 === 0 ? 'rgba(0,0,0,0.02)' : 'transparent',
                            borderRadius: '8px',
                            mb: 0.5,
                            display: 'block'
                          }}
                        >
                          {/* Header with date and template name if applicable */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {format(new Date(checkIn.date || checkIn.createdAt), 'MMMM d, yyyy')}
                            </Typography>
                            {isTemplateBased && checkIn.templateName && (
                              <Chip
                                label={checkIn.templateName}
                                size="small"
                                sx={{
                                  height: '20px',
                                  fontSize: '0.7rem',
                                  background: 'rgba(33, 150, 243, 0.1)',
                                  color: '#1976D2'
                                }}
                              />
                            )}
                            {!isTemplateBased && (
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Typography variant="body1" title="Mood">
                                  {checkIn.moodRating !== undefined && checkIn.moodRating !== null
                                    ? getMoodEmoji(checkIn.moodRating)
                                    : '❓'}
                                </Typography>
                                <Typography variant="body1" title="Anxiety">
                                  {checkIn.anxietyRating !== undefined && checkIn.anxietyRating !== null
                                    ? getAnxietyEmoji(checkIn.anxietyRating)
                                    : '❓'}
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          {/* Content based on check-in type */}
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {isTemplateBased ? (
                              // Template-based check-in: show dynamic responses
                              <>
                                {checkIn.dynamicResponses
                                  .filter(r => r.fieldId !== 'notes') // Notes shown separately
                                  .map((response, rIdx) => {
                                    const value = formatDynamicResponse(response);
                                    const isBooleanTrue = response.fieldType === 'boolean' && response.value === true;
                                    const isBooleanFalse = response.fieldType === 'boolean' && response.value === false;

                                    return (
                                      <Chip
                                        key={rIdx}
                                        icon={isBooleanTrue ? <CheckCircleIcon sx={{ fontSize: 14 }} /> :
                                              isBooleanFalse ? <CancelIcon sx={{ fontSize: 14 }} /> : undefined}
                                        label={`${getShortLabel(response.fieldId)}: ${value}`}
                                        size="small"
                                        sx={{
                                          height: '24px',
                                          fontSize: '0.75rem',
                                          background: isBooleanTrue ? 'rgba(76, 175, 80, 0.1)' :
                                                      isBooleanFalse ? 'rgba(158, 158, 158, 0.1)' :
                                                      'rgba(33, 150, 243, 0.1)',
                                          color: isBooleanTrue ? '#4CAF50' :
                                                 isBooleanFalse ? '#9E9E9E' :
                                                 '#1976D2'
                                        }}
                                      />
                                    );
                                  })}
                                {/* Show notes if present */}
                                {checkIn.notes && (
                                  <Typography variant="caption" sx={{
                                    ml: 1,
                                    color: 'text.secondary',
                                    alignSelf: 'center',
                                    fontStyle: 'italic'
                                  }}>
                                    "{checkIn.notes}"
                                  </Typography>
                                )}
                              </>
                            ) : (
                              // Legacy check-in: show mood/anxiety chips
                              <>
                                {checkIn.moodRating !== undefined && checkIn.moodRating !== null && (
                                  <Chip
                                    label={`Mood: ${checkIn.moodRating}/5`}
                                    size="small"
                                    sx={{
                                      height: '24px',
                                      fontSize: '0.75rem',
                                      background: 'rgba(255, 140, 0, 0.1)',
                                      color: dashboardTheme.colors.primary.main
                                    }}
                                  />
                                )}
                                {checkIn.anxietyRating !== undefined && checkIn.anxietyRating !== null && (
                                  <Chip
                                    label={`Anxiety: ${checkIn.anxietyRating}/5`}
                                    size="small"
                                    sx={{
                                      height: '24px',
                                      fontSize: '0.75rem',
                                      background: `${getAnxietyColor(checkIn.anxietyRating)}15`,
                                      color: getAnxietyColor(checkIn.anxietyRating)
                                    }}
                                  />
                                )}
                                {checkIn.notes && (
                                  <Typography variant="caption" sx={{
                                    ml: 1,
                                    color: 'text.secondary',
                                    alignSelf: 'center'
                                  }}>
                                    {checkIn.notes}
                                  </Typography>
                                )}
                              </>
                            )}
                          </Box>
                        </ListItem>
                      );
                    })}
                  </List>
                </>
              );
            })()}
          </Box>
        ) : (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <HistoryIcon sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No check-in history yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Start tracking your mood daily
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ borderRadius: '12px' }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckInHistoryDialog;